class SiteNavDrawer extends HTMLElement {
  static FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  connectedCallback() {
    this.overlay = this.querySelector("[data-nav-overlay]");
    this.panel = this.querySelector("[data-nav-panel]");
    this.triggerElement = null;

    this.handleClose = this.close.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleClick = this.onClick.bind(this);
    this.handleDocClick = this.onDocClick.bind(this);

    this.overlay.addEventListener("click", this.handleClose);
    this.addEventListener("click", this.handleClick);
    document.addEventListener("click", this.handleDocClick);
  }

  disconnectedCallback() {
    this.overlay.removeEventListener("click", this.handleClose);
    this.removeEventListener("click", this.handleClick);
    document.removeEventListener("click", this.handleDocClick);
    document.removeEventListener("keydown", this.handleKeydown);
  }

  // -- Accessibility --

  trapFocus(e) {
    const focusable = [...this.panel.querySelectorAll(SiteNavDrawer.FOCUSABLE)];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // -- Open / Close --

  open() {
    this.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeydown);

    const onTransitionEnd = () => {
      this.panel.removeEventListener("transitionend", onTransitionEnd);
      const closeBtn = this.querySelector("[data-nav-close]");
      if (closeBtn) closeBtn.focus();
    };
    this.panel.addEventListener("transitionend", onTransitionEnd);
  }

  close() {
    this.removeAttribute("data-open");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeydown);

    // Close all open accordions
    for (const accordion of this.querySelectorAll("[data-nav-accordion]")) {
      this.collapseAccordion(accordion);
    }

    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null;
    }
  }

  // -- Accordion --

  toggleAccordion(accordion) {
    const trigger = accordion.querySelector("[data-nav-accordion-trigger]");
    const content = accordion.querySelector("[data-nav-accordion-content]");
    const icon = accordion.querySelector("[data-nav-accordion-icon]");
    const isOpen = trigger.getAttribute("aria-expanded") === "true";

    if (isOpen) {
      this.collapseAccordion(accordion);
    } else {
      trigger.setAttribute("aria-expanded", "true");
      content.setAttribute("aria-hidden", "false");
      content.style.gridTemplateRows = "1fr";
      if (icon) icon.style.transform = "rotate(45deg)";
    }
  }

  collapseAccordion(accordion) {
    const trigger = accordion.querySelector("[data-nav-accordion-trigger]");
    const content = accordion.querySelector("[data-nav-accordion-content]");
    const icon = accordion.querySelector("[data-nav-accordion-icon]");

    trigger.setAttribute("aria-expanded", "false");
    content.setAttribute("aria-hidden", "true");
    content.style.gridTemplateRows = "0fr";
    if (icon) icon.style.transform = "";
  }

  // -- Event handlers --

  onKeydown(e) {
    if (e.key === "Escape") {
      this.close();
      return;
    }
    if (e.key === "Tab") {
      this.trapFocus(e);
    }
  }

  onDocClick(e) {
    const toggle = e.target.closest("[data-nav-toggle]");
    if (!toggle) return;
    e.preventDefault();
    this.triggerElement = toggle;
    this.open();
  }

  onClick(e) {
    if (e.target.closest("[data-nav-close]")) {
      this.close();
      return;
    }

    const trigger = e.target.closest("[data-nav-accordion-trigger]");
    if (trigger) {
      const accordion = trigger.closest("[data-nav-accordion]");
      if (accordion) this.toggleAccordion(accordion);
    }
  }
}

if (!customElements.get("site-nav-drawer")) {
  customElements.define("site-nav-drawer", SiteNavDrawer);
}
